import * as React from "react"
import styled from "@emotion/styled"
import { useIntl } from "react-intl"
import { supportedLanguages } from "../utils/languages"
import allLanguages from "../utils/languages"
import StyledIcon from "./StyledIcon"
import NakedButton from "./NakedButton"

interface IProps {
  variant?: "nav" | "mobile"
}

const LanguageButton = styled(NakedButton)`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  background: transparent;
  color: ${(props) => props.theme.colors.text};
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;
  transition: border-color 0.15s, color 0.15s;
  margin-inline-start: 0.75rem;
  &:hover {
    color: ${(props) => props.theme.colors.primary};
    border-color: ${(props) => props.theme.colors.primary};
  }
`

const DropdownWrap = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownMenu = styled.ul`
  position: absolute;
  top: calc(100% + 0.7rem);
  inset-inline-end: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.text};
  border-radius: 0.375rem;
  min-width: 11rem;
  max-height: 21rem;
  overflow-y: auto;
  z-index: 1100;
`

const DropdownItem = styled.button<{ active: boolean }>`
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  text-align: start;
  border: none;
  background: transparent;
  color: ${(props) =>
    props.active
      ? props.theme.colors.primary
      : props.theme.colors.text};
  font-size: 0.85rem;
  font-weight: ${(props) => (props.active ? "bold" : "normal")};
  font-family: inherit;
  cursor: pointer;
  transition: color 0.15s, background-color 0.15s;
  &:hover {
    color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.dropdownBackgroundHover};
  }
`

const MobileLanguageItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.1rem;
  font-weight: 600;
  font-family: inherit;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 100%;
  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`

const MobileLanguageList = styled.ul`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
`

const MobileLanguageOption = styled.button<{ active: boolean }>`
  display: block;
  width: 100%;
  text-align: start;
  padding: 0.5rem 0.5rem;
  border: none;
  background: transparent;
  color: ${(props) =>
    props.active ? props.theme.colors.primary : props.theme.colors.text};
  font-size: 1rem;
  font-weight: ${(props) => (props.active ? "bold" : "normal")};
  font-family: inherit;
  cursor: pointer;
  border-radius: 0.375rem;
  transition: color 0.15s, background-color 0.15s;
  &:hover {
    color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.dropdownBackgroundHover};
  }
`

const ChevronIcon = styled(StyledIcon)<{ open: boolean }>`
  transition: transform 0.2s;
  transform: rotate(${(props) => (props.open ? "180deg" : "0deg")});
`

const langPrefixRe = new RegExp(
  `^\\/(${supportedLanguages.sort((a, b) => b.length - a.length).join("|")})(\\/|$)`,
)

function usePagePath(): string {
  const [path, setPath] = React.useState("")
  React.useEffect(() => {
    setPath(window.location.pathname.replace(langPrefixRe, "/"))
  }, [])
  return path
}

const LanguageSelector: React.FC<IProps> = ({ variant = "nav" }) => {
  const intl = useIntl()
  const currentLang = intl.locale.toString()
  const currentPagePath = usePagePath()
  const hasMultipleLangs = supportedLanguages.length > 1

  if (variant === "mobile") {
    const [open, setOpen] = React.useState(false)
    return (
      <>
        <MobileLanguageItem onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <StyledIcon name="language" size="20" />
          <span>{currentLang.toUpperCase()}</span>
          <ChevronIcon name="chevron" size="16" open={open} />
        </MobileLanguageItem>
        {open && (
          <MobileLanguageList>
            {supportedLanguages.map((langCode) => {
              const isActive = langCode === currentLang
              const lang = allLanguages[langCode]
              return (
                <MobileLanguageOption
                  key={langCode}
                  active={isActive}
                  onClick={() => {
                    window.location.href = `/${langCode}${currentPagePath}`
                  }}
                >
                  {lang.localName} ({langCode.toUpperCase()})
                </MobileLanguageOption>
              )
            })}
          </MobileLanguageList>
        )}
      </>
    )
  }

  if (!hasMultipleLangs) {
    return (
      <LanguageButton aria-label="Language" title="Language">
        <StyledIcon name="language" size="16" />
        <span>{currentLang.toUpperCase()}</span>
      </LanguageButton>
    )
  }

  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  return (
    <DropdownWrap ref={ref}>
      <LanguageButton
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <StyledIcon name="language" size="16" />
        <span>{currentLang.toUpperCase()}</span>
        <ChevronIcon name="chevron" size="16" open={open} />
      </LanguageButton>
      {open && (
        <DropdownMenu>
          {supportedLanguages.map((langCode) => {
            const isActive = langCode === currentLang
            const lang = allLanguages[langCode]
            return (
              <DropdownItem
                key={langCode}
                active={isActive}
                onClick={() => {
                  window.location.href = `/${langCode}${currentPagePath}`
                }}
              >
                {lang.localName} ({langCode.toUpperCase()})
              </DropdownItem>
            )
          })}
        </DropdownMenu>
      )}
    </DropdownWrap>
  )
}

export default LanguageSelector
